<template>
  <div class="gallery-wrapper">
    <Transition name="fade">
      <div class="thumbnails">
        <div class="thumbnails__inner">
          <button
            v-for="(image, index) in images"
            :key="image.id"
            class="thumbnail"
            :class="{ 'thumbnail--active': selectedImage === index }"
            @click="goToDot(index)"
          >
            <img
              v-if="image.mediaContentType === 'VIDEO'"
              :src="image.previewImage.url"
              class="thumbnail__image"
            />
            <img v-else :src="image.thumbnail" class="thumbnail__image" />
          </button>
        </div>
      </div>
    </Transition>
    <div ref="gallery" class="gallery">
      <div v-for="image in images" :key="image.id" ref="galleryItems">
        <Media
          :images="image"
          :sources="image.sources"
          cover
          @click="handleImageClick(image)"
        />
      </div>
    </div>

    <ToastMessage class="toast"> Tap to zoom </ToastMessage>
  </div>

  <div class="next-prev">
    <button class="prev" @click="goToDot(selectedImage - 1)">
      <i class="icon icon-next-left"></i>
    </button>
    <button class="next" @click="goToDot(selectedImage + 1)">
      <i class="icon icon-next-right"></i>
    </button>
  </div>

  <div class="dots-container">
    <div class="dots">
      <span
        v-for="(item, index) in images"
        :key="item.id"
        class="dot"
        :class="{ 'dot--active': intersectStatus[index] }"
      ></span>
    </div>
  </div>

  <ImageViewer
    v-if="isImageViewerOpen"
    :image="imageViewerImage"
    @close="isImageViewerOpen = false"
  />
</template>

<script>
import Media from "@/components/Media.vue";
import ImageViewer from "@/components/product-display/ImageViewer.vue";
import { mapState } from "vuex";
import { productImageSequence } from "@/helpers/imageSequences.js";
import ToastMessage from "@/components/ToastMessage.vue";

export default {
  name: "ProductMediaGallery",
  components: {
    Media,
    ImageViewer,
    ToastMessage,
  },
  data() {
    return {
      observers: [],
      intersectStatus: [],
      isImageViewerOpen: false,
      selectedImage: null,
    };
  },
  computed: {
    ...mapState("product", ["product"]),
    images() {
      const media = productImageSequence
        .map((sequence) => {
          let mediaItem;

          if (sequence.includes("Video")) {
            mediaItem = this.getFirstReference(
              this.product[sequence]?.references.edges
            );
          } else {
            mediaItem = this.getFirstReference(
              this.product[sequence]?.references?.edges
            )?.image;
          }

          if (!mediaItem) {
            return null;
          }

          return mediaItem;
        })
        .filter((mediaItem) => mediaItem !== null);

      return media;
    },
  },
  mounted() {
    this.intersectStatus = Array(this.$refs.galleryItems.length).fill(false);

    // setup the observers for each gallery item
    this.$refs.galleryItems.forEach((item, index) => {
      this.setupObserver(item, index);
    });

    // // Make sure first index is the first slide
    setTimeout(() => {
      this.$refs.gallery.scrollTo(0, 0);
    }, 1000);
  },
  beforeUnmount() {
    this.observers.forEach((observer) => observer.disconnect());
  },
  methods: {
    setupObserver(element, index) {
      const options = {
        root: this.$refs.gallery,
        rootMargin: "0px",
        threshold: 0.9,
      };

      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.intersectStatus[index] = true;
        } else {
          this.intersectStatus[index] = false;
        }
      }, options);

      observer.observe(element);
      this.observers.push(observer);
    },
    goToDot(index) {
      if (index < 0) {
        this.selectedImage = this.images.length - 1;
      } else if (index >= this.images.length) {
        this.selectedImage = 0;
      } else {
        this.selectedImage = index;
      }

      const galleryChildren = this.$refs.gallery.children;
      const selectedChild = galleryChildren[this.selectedImage];

      if (selectedChild) {
        const gallery = this.$refs.gallery;
        const galleryRect = gallery.getBoundingClientRect();
        const childRect = selectedChild.getBoundingClientRect();

        const scrollLeft =
          gallery.scrollLeft +
          childRect.left -
          galleryRect.left -
          (galleryRect.width - childRect.width) / 2;

        gallery.scrollTo({
          left: scrollLeft,
          behavior: "smooth",
        });
      }
    },
    handleImageClick(image) {
      if (image.sources?.length > 0) {
        return;
      }

      this.imageViewerImage = {
        blurry: image.thumbnail,
        url: image.imageViewerDesktop,
      };

      this.isImageViewerOpen = true;
    },
    getFirstReference(references) {
      if (!references) return null;
      const nodes = references.map((reference) => reference.node);
      return nodes[0];
    },
  },
};
</script>

<style lang="scss" scoped>
.gallery {
  display: flex;
  overflow: auto;
  width: 100%;
  flex-flow: row nowrap;
  scroll-snap-type: x mandatory;
  position: relative;

  &::-webkit-scrollbar {
    display: none;
  }

  & > :deep(*) {
    scroll-snap-align: center;
    scroll-snap-stop: always;
    flex: none;
    width: 100%;
    overflow: hidden;
    cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' fill='none'%3E%3Cpath fill='%23000' d='M27 21h-6v6h-2v-6h-6v-2h6v-6h2v6h6v2Z'/%3E%3Crect width='39' height='39' x='.5' y='.5' stroke='%23070707' rx='19.5'/%3E%3C/svg%3E")
        20 20,
      pointer;
  }
}

.gallery-wrapper {
  position: relative;
  display: flex;

  .toast {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: 30px;
    text-align: center;

    @include respond-above(md) {
      display: none;
    }
  }
}

.thumbnails {
  display: none;
  position: relative;
  top: 0px;
  left: 0px;
  z-index: 1;
  margin-right: 33px;

  @include respond-above(xl) {
    display: block;
  }

  &__inner {
    width: 90px;
  }

  .thumbnail {
    margin-bottom: 8px;
    background: none;
    outline: none;
    padding: 4px;
    border: 1px solid transparent;

    &__image {
      display: block;
      max-width: 100%;
      height: auto;
    }

    &--active {
      border: 1px solid $color-black;
      border-radius: 4px;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.dots {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  background-color: rgba(128, 128, 128, 0.2);
  backdrop-filter: blur(8px);
  border-radius: 50px;

  @include respond-above(lg) {
    display: none;
  }

  & > .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.3);
    margin: 0 4px;
    transition: all 0.3s ease;

    &.dot--active {
      background-color: $color-black;
    }
  }
}

.dots-container {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
}

.next-prev {
  display: none;
  position: absolute;
  top: 25%;
  width: 100%;
  padding: 0 20px;
  justify-content: space-between;

  @include respond-above(lg) {
    display: flex;
  }

  @include respond-above(xl) {
    display: none;
  }

  button {
    border: none;
    outline: none;
    cursor: pointer;
    background-color: $color-white;
    z-index: 10;
    width: 40px;
    height: 40px;
    border-radius: 50%;

    i {
      color: $color-black;
    }
  }

  .prev > i {
    margin-right: 2px;
  }

  .next > i {
    margin-left: 2px;
  }
}
</style>
